import {
  SIGNUP_ATTRIBUTION_METADATA_KEY,
  SIGNUP_LAST_ATTRIBUTION_METADATA_KEY,
} from "@/lib/signup-attribution";
import { isSuppressedUrlString } from "@/lib/analytics/suppressed-paths";

// Attribution fields that hold a URL and could carry a bearer token. This is
// the shared read boundary for stored, current-URL, and merged attribution, so
// dropping token-bearing values here scrubs every sink (Clerk unsafeMetadata,
// the attribution DB) AND cleans data captured/stored before the fix.
const URL_ATTRIBUTION_KEYS = new Set(["landingPage", "sourcePath", "referrer"]);

const MAX_ATTRIBUTION_VALUE_LENGTH = 500;

export const ATTRIBUTION_FIELDS = [
  { key: "utmSource", dbKey: "utmSource", stripeKey: "utm_source" },
  { key: "utmMedium", dbKey: "utmMedium", stripeKey: "utm_medium" },
  { key: "utmCampaign", dbKey: "utmCampaign", stripeKey: "utm_campaign" },
  { key: "utmContent", dbKey: "utmContent", stripeKey: "utm_content" },
  { key: "utmTerm", dbKey: "utmTerm", stripeKey: "utm_term" },
  { key: "utmOffer", dbKey: "utmOffer", stripeKey: "utm_offer" },
  { key: "ref", dbKey: "refParam", stripeKey: "ref" },
  { key: "referrer", dbKey: "referrer", stripeKey: "referrer" },
  { key: "landingPage", dbKey: "landingPage", stripeKey: "landing_page" },
  { key: "gclid", dbKey: "gclid", stripeKey: "gclid" },
  { key: "gbraid", dbKey: "gbraid", stripeKey: "gbraid" },
  { key: "wbraid", dbKey: "wbraid", stripeKey: "wbraid" },
  { key: "msclkid", dbKey: "msclkid", stripeKey: "msclkid" },
  { key: "gadSource", dbKey: "gadSource", stripeKey: "gad_source" },
  { key: "gadCampaignId", dbKey: "gadCampaignId", stripeKey: "gad_campaignid" },
  {
    key: "capturedAt",
    dbKey: "attributionCapturedAt",
    stripeKey: "attribution_captured_at",
    kind: "datetime",
  },
  { key: "sourcePath", dbKey: "attributionSourcePath", stripeKey: "attribution_source_path" },
  { key: "visitorId", dbKey: "attributionVisitorId", stripeKey: "attribution_visitor_id" },
  { key: "gaClientId", dbKey: "gaClientId", stripeKey: "ga_client_id" },
  { key: "posthogAnonId", dbKey: "posthogAnonId", stripeKey: "ph_anon_id" },
] as const;

export type AttributionKey = (typeof ATTRIBUTION_FIELDS)[number]["key"];
export type AttributionDbKey = (typeof ATTRIBUTION_FIELDS)[number]["dbKey"];
type AttributionStringDbKey = Exclude<AttributionDbKey, "attributionCapturedAt">;
export type Attribution = Partial<Record<AttributionKey, string>>;
export type AttributionDbFields = Partial<Record<AttributionStringDbKey, string>> & {
  attributionCapturedAt?: Date;
};

export const ATTRIBUTION_DB_SELECT = Object.fromEntries(
  ATTRIBUTION_FIELDS.map((field) => [field.dbKey, true]),
) as Record<AttributionDbKey, true>;

export const ATTRIBUTION_DB_KEYS = ATTRIBUTION_FIELDS.map(
  (field) => field.dbKey,
) as readonly AttributionDbKey[];

const ACQUISITION_ATTRIBUTION_DB_KEYS = ATTRIBUTION_FIELDS.filter(
  (field) =>
    field.key !== "landingPage" &&
    field.key !== "capturedAt" &&
    field.key !== "sourcePath" &&
    field.key !== "visitorId" &&
    field.key !== "gaClientId" &&
    field.key !== "posthogAnonId",
).map((field) => field.dbKey) as readonly AttributionDbKey[];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function sanitizeAttributionValue(value: unknown): string | null {
  if (value instanceof Date) {
    const time = value.getTime();
    return Number.isNaN(time) ? null : value.toISOString();
  }

  if (typeof value !== "string") return null;

  const trimmed = value.trim();
  if (!trimmed) return null;

  if (trimmed.length <= MAX_ATTRIBUTION_VALUE_LENGTH) return trimmed;
  return `${trimmed.slice(0, MAX_ATTRIBUTION_VALUE_LENGTH - 3)}...`;
}

function extractAttributionRecord(
  input: unknown,
  metadataKey: string,
): Record<string, unknown> | null {
  if (!isRecord(input)) return null;

  const nested = input[metadataKey];
  if (isRecord(nested)) return nested;

  const attribution = input.attribution;
  if (isRecord(attribution)) return attribution;

  return input;
}

function normalizeAttributionRecord(record: Record<string, unknown> | null): Attribution | null {
  if (!record) return null;

  const attribution: Attribution = {};
  for (const field of ATTRIBUTION_FIELDS) {
    const value = sanitizeAttributionValue(record[field.key]);
    if (!value) continue;
    if (URL_ATTRIBUTION_KEYS.has(field.key) && isSuppressedUrlString(value)) {
      continue;
    }
    attribution[field.key] = value;
  }

  return Object.keys(attribution).length > 0 ? attribution : null;
}

export function normalizeAttribution(input: unknown): Attribution | null {
  return normalizeAttributionRecord(
    extractAttributionRecord(input, SIGNUP_ATTRIBUTION_METADATA_KEY),
  );
}

export function normalizeLastAttribution(input: unknown): Attribution | null {
  return normalizeAttributionRecord(
    extractAttributionRecord(input, SIGNUP_LAST_ATTRIBUTION_METADATA_KEY),
  );
}

export function mergeAttribution(...sources: unknown[]): Attribution | null {
  const attribution: Attribution = {};

  for (const source of sources) {
    const normalized = normalizeAttribution(source);
    if (normalized) Object.assign(attribution, normalized);
  }

  return Object.keys(attribution).length > 0 ? attribution : null;
}

const PAID_MEDIUM_PATTERN = /^(cpc|ppc|paid|paid_search|paid-search|sem)$/i;
const ACQUISITION_KEYS: readonly AttributionKey[] = [
  "utmSource",
  "utmMedium",
  "utmCampaign",
  "utmContent",
  "utmTerm",
  "utmOffer",
  "ref",
  "referrer",
  "gclid",
  "gbraid",
  "wbraid",
  "msclkid",
  "gadSource",
  "gadCampaignId",
];
const ANALYTICS_IDENTITY_KEYS: readonly AttributionKey[] = [
  "visitorId",
  "gaClientId",
  "posthogAnonId",
];

function isPaidAttribution(attribution: Attribution | null): attribution is Attribution {
  if (!attribution) return false;
  return Boolean(
    attribution.gclid ||
      attribution.gbraid ||
      attribution.wbraid ||
      attribution.msclkid ||
      attribution.gadCampaignId ||
      (attribution.utmMedium && PAID_MEDIUM_PATTERN.test(attribution.utmMedium)),
  );
}

function hasAcquisitionAttribution(attribution: Attribution | null): boolean {
  return Boolean(
    attribution && ACQUISITION_KEYS.some((key) => Boolean(attribution[key])),
  );
}

/**
 * Select one conversion touch as an indivisible unit, with sources ordered
 * oldest to newest. Field-wise merging can manufacture a touch that never
 * happened (for example an old gclid beside a newer wbraid). The newest paid
 * touch wins, then the newest non-paid acquisition touch. Analytics identity
 * values are the only fields carried across touches because they identify the
 * same browser rather than describe how that touch was acquired.
 */
export function selectConversionAttribution(...sources: unknown[]): Attribution | null {
  const normalized = sources.map(normalizeAttribution);
  const selectedTouch =
    normalized.findLast(isPaidAttribution) ??
    normalized.findLast(hasAcquisitionAttribution) ??
    normalized.findLast((source): source is Attribution => source != null) ??
    null;

  const identity: Attribution = {};
  for (const source of normalized) {
    if (!source) continue;
    for (const key of ANALYTICS_IDENTITY_KEYS) {
      const value = source[key];
      if (value) identity[key] = value;
    }
  }

  return mergeAttribution(selectedTouch, identity);
}

export function attributionToDbFields(input: unknown): AttributionDbFields {
  const attribution = normalizeAttribution(input);
  if (!attribution) return {};

  const fields: AttributionDbFields = {};
  for (const field of ATTRIBUTION_FIELDS) {
    const value = attribution[field.key];
    if (!value) continue;
    if (field.dbKey === "attributionCapturedAt") {
      const date = new Date(value);
      if (!Number.isNaN(date.getTime())) fields.attributionCapturedAt = date;
      continue;
    }
    fields[field.dbKey as AttributionStringDbKey] = value;
  }
  return fields;
}

export function attributionFromDbFields(input: unknown): Attribution | null {
  if (!isRecord(input)) return null;

  const record: Record<string, unknown> = {};
  for (const field of ATTRIBUTION_FIELDS) {
    record[field.key] = input[field.dbKey];
  }

  // Persisted columns can pre-date the token-URL capture fix. Route them back
  // through the same normalization boundary as browser/JSON attribution so a
  // legacy bearer credential cannot reach Slack, email, admin, or Stripe.
  return normalizeAttributionRecord(record);
}

/**
 * Human-readable "where did this lead come from" summary, in a fixed display
 * order. Surfaces only the acquisition-meaningful fields (UTMs, Google click
 * id, ad source, referral code, referrer, landing page) and only the ones that
 * are present — the technical ids (visitorId, gaClientId, posthogAnonId, braid
 * params) are deliberately omitted. This is the single source of truth for the
 * ops-facing attribution display shared by the admin Site Control leads page,
 * the Slack lead alert, and the ops handoff email, so the three never drift.
 */
const ATTRIBUTION_SUMMARY_FIELDS: readonly { key: AttributionKey; label: string }[] = [
  { key: "utmSource", label: "Source" },
  { key: "utmMedium", label: "Medium" },
  { key: "utmCampaign", label: "Campaign" },
  { key: "utmContent", label: "Content" },
  { key: "utmTerm", label: "Term" },
  { key: "gclid", label: "Google Click ID" },
  { key: "msclkid", label: "Microsoft Click ID" },
  { key: "gadSource", label: "Google Ads source" },
  { key: "ref", label: "Referral code" },
  { key: "referrer", label: "Referrer" },
  { key: "landingPage", label: "Landing page" },
] as const;

export function attributionSummaryParts(
  attribution: Attribution | null | undefined,
): { label: string; value: string }[] {
  // Defence in depth for legacy JSON columns or callers that did not use a
  // persisted-field reader first.
  const normalized = normalizeAttribution(attribution);
  if (!normalized) return [];
  const parts: { label: string; value: string }[] = [];
  for (const field of ATTRIBUTION_SUMMARY_FIELDS) {
    const value = normalized[field.key];
    if (value) parts.push({ label: field.label, value });
  }
  return parts;
}

export function attributionToJson(input: unknown): Record<string, string> | null {
  const attribution = normalizeAttribution(input);
  if (!attribution) return null;

  const json: Record<string, string> = {};
  for (const field of ATTRIBUTION_FIELDS) {
    const value = attribution[field.key];
    if (value) json[field.key] = value;
  }
  return Object.keys(json).length > 0 ? json : null;
}

export function attributionToStripeMetadata(
  input: unknown,
  gaCookie?: string | null,
): Record<string, string> {
  const metadata: Record<string, string> = {};
  const cleanGaCookie = sanitizeAttributionValue(gaCookie);
  if (cleanGaCookie) metadata.ga_cookie = cleanGaCookie;

  const attribution = normalizeAttribution(input);
  if (!attribution) return metadata;

  for (const field of ATTRIBUTION_FIELDS) {
    const value = attribution[field.key];
    if (value) metadata[field.stripeKey] = value;
  }

  return metadata;
}

export function attributionFromStripeMetadata(
  metadata: Record<string, string> | null | undefined,
): Attribution | null {
  if (!metadata) return null;

  const record: Record<string, unknown> = {};
  for (const field of ATTRIBUTION_FIELDS) {
    record[field.key] = metadata[field.stripeKey];
  }

  return normalizeAttributionRecord(record);
}

export function attributionToAnalyticsProperties(
  input: unknown,
): Record<string, string> {
  const attribution = normalizeAttribution(input);
  if (!attribution) return {};

  const properties: Record<string, string> = {};
  for (const field of ATTRIBUTION_FIELDS) {
    const value = attribution[field.key];
    if (value) properties[field.key] = value;
  }
  return properties;
}

/**
 * Drop any field the stored record already has a value for.
 *
 * `hasStoredAttribution()` deliberately ignores the non-acquisition fields
 * (`sourcePath`, `landingPage`, the analytics ids), so a visitor carrying only
 * a landing path reads as having NO attribution. Callers that adopt a fresh
 * attribution set on the strength of that answer would then overwrite the path
 * they already had — silently losing first touch for ordinary direct traffic,
 * which is the largest cohort with no campaign parameters.
 *
 * Adopting per-field instead keeps first touch true for every column: a value
 * already recorded stays, and only genuinely empty fields are filled.
 *
 * Pinned by __tests__/lib/attribution-first-touch-fill.test.ts.
 */
export function attributionFieldsToFill(
  incoming: AttributionDbFields,
  stored: unknown,
): AttributionDbFields {
  if (!isRecord(stored)) return incoming;

  const filtered: AttributionDbFields = {};
  for (const [key, value] of Object.entries(incoming)) {
    const existing = stored[key];
    const alreadySet =
      existing instanceof Date ||
      (typeof existing === "string" && existing.trim().length > 0);
    if (alreadySet) continue;
    if (key === "attributionCapturedAt") {
      if (value instanceof Date) filtered.attributionCapturedAt = value;
      continue;
    }
    if (typeof value === "string") {
      filtered[key as AttributionStringDbKey] = value;
    }
  }
  return filtered;
}

export function hasStoredAttribution(input: unknown): boolean {
  if (!isRecord(input)) return false;
  return ACQUISITION_ATTRIBUTION_DB_KEYS.some((field) => {
    const value = input[field];
    return typeof value === "string" && value.trim().length > 0;
  });
}
